import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { 
  ArrowLeft, 
  Calendar, 
  ChevronDown, 
  ChevronLeft, 
  ChevronRight, 
  Clock,
  Download, 
  Edit2, 
  Eye, 
  Globe,
  MessageSquare, 
  Monitor,
  MousePointer,
  RefreshCw,
  Save, 
  ShoppingCart,
  Smartphone,
  Tablet,
  TrendingDown, 
  TrendingUp, 
  Users, 
  Bot,
  X 
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { spacing, borderRadius, fontSize, fontWeight, shadows } from '../../styles/theme';
import WebvonkLoader from '../components/WebvonkLoader';
import { getKmfcClient } from '../../lib/supabase';
import { usePermissions } from '../hooks/usePermissions';
import { GoogleGenerativeAI } from '@google/generative-ai';

// ─── Styled Components ──────────────────────────────────────────────────────────────
const Container = styled.div`
  height: 100vh;
  background-color: ${props => props.theme.colors.background};
  overflow: hidden;
`;

const Content = styled.div`
  padding: ${spacing.xl};
  height: calc(100vh - 52px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  
  @media (max-width: 768px) {
    padding: ${spacing.md};
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.md};
  margin-bottom: ${spacing.xl};
`;


const Title = styled.h1`
  font-size: clamp(24px, 3vw, 32px);
  font-weight: ${fontWeight.bold};
  color: ${props => props.theme.colors.text};
  letter-spacing: -0.02em;
  line-height: 1;
  margin: 0;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${spacing.md};
  margin-bottom: ${spacing.xl};
`;

const MetricCard = styled.div`
  background-color: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${borderRadius.lg};
  padding: ${spacing.lg};
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.02) 0%, transparent 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${shadows.md};
    
    &::before {
      opacity: 1;
    }
  }
  
  ${props => props.accent && `
    border-top: 3px solid ${props.accent};
  `}
`;

const MetricIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${borderRadius.md};
  background-color: ${props => props.accent}20;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${spacing.md};
  
  svg {
    color: ${props => props.accent};
  }
`;

const MetricLabel = styled.div`
  font-size: ${fontSize.sm};
  color: ${props => props.theme.colors.muted};
  margin-bottom: ${spacing.xs};
  font-weight: ${fontWeight.medium};
`;

const MetricValue = styled.div`
  font-size: 28px;
  font-weight: ${fontWeight.bold};
  color: ${props => props.theme.colors.text};
  letter-spacing: -0.02em;
  line-height: 1;
`;

const MetricStatus = styled.div`
  font-size: 11px;
  font-weight: ${fontWeight.medium};
  color: ${props => props.color || props.theme.colors.muted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 4px;
`;

const Section = styled.div`
  margin-bottom: ${spacing.xl};
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: ${fontWeight.semibold};
  color: ${props => props.theme.colors.text};
  margin-bottom: ${spacing.lg};
`;

const InfoCard = styled.div`
  background-color: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${borderRadius.lg};
  padding: ${spacing.xl};
  text-align: center;
`;

const InfoText = styled.p`
  font-size: ${fontSize.md};
  color: ${props => props.theme.colors.muted};
  line-height: 1.6;
  margin: 0;
`;

const WidgetGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${spacing.lg};
  margin-bottom: ${spacing.xl};
`;

const WidgetCard = styled.div`
  background-color: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${borderRadius.lg};
  padding: ${spacing.lg};
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${shadows.md};
  }
`;

const WidgetHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${spacing.md};
`;

const WidgetTitle = styled.h3`
  font-size: ${fontSize.md};
  font-weight: ${fontWeight.semibold};
  color: ${props => props.theme.colors.text};
  margin: 0;
`;

const WidgetIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: ${borderRadius.sm};
  background-color: ${props => props.accent}20;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    color: ${props => props.accent};
  }
`;

const DeviceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${spacing.sm};
  margin-top: ${spacing.md};
`;

const DeviceItem = styled.div`
  text-align: center;
  padding: ${spacing.sm};
  background-color: ${props => props.theme.colors.lightGray};
  border-radius: ${borderRadius.sm};
`;

const DeviceIcon = styled.div`
  margin-bottom: ${spacing.xs};
  
  svg {
    color: ${props => props.theme.colors.muted};
  }
`;

const DeviceName = styled.div`
  font-size: ${fontSize.xs};
  color: ${props => props.theme.colors.muted};
  margin-bottom: 2px;
`;

const DevicePercent = styled.div`
  font-size: ${fontSize.sm};
  font-weight: ${fontWeight.semibold};
  color: ${props => props.theme.colors.text};
`;

const PageList = styled.div`
  margin-top: ${spacing.md};
`;

const PageItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${spacing.sm} 0;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  
  &:last-child {
    border-bottom: none;
  }
`;

const PageUrl = styled.div`
  font-size: ${fontSize.sm};
  color: ${props => props.theme.colors.text};
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PageViews = styled.div`
  font-size: ${fontSize.sm};
  font-weight: ${fontWeight.semibold};
  color: ${props => props.theme.colors.text};
  margin-left: ${spacing.sm};
`;

const TimeBar = styled.div`
  height: 8px;
  background-color: ${props => props.theme.colors.lightGray};
  border-radius: 4px;
  overflow: hidden;
  margin-top: ${spacing.md};
`;

const TimeSegment = styled.div`
  height: 100%;
  background-color: ${props => props.accent};
  transition: width 0.3s ease;
`;

const ReferrerList = styled.div`
  margin-top: ${spacing.md};
`;

const ReferrerItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${spacing.sm} 0;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  
  &:last-child {
    border-bottom: none;
  }
`;

const ReferrerName = styled.div`
  font-size: ${fontSize.sm};
  color: ${props => props.theme.colors.text};
  flex: 1;
`;

const ReferrerCount = styled.div`
  font-size: ${fontSize.sm};
  font-weight: ${fontWeight.semibold};
  color: ${props => props.theme.colors.text};
`;

// ─── Popup Components ───────────────────────────────────────────────────────────────
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${spacing.lg};
`;

const Popup = styled.div`
  background-color: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${borderRadius.xl};
  padding: ${spacing.xl};
  max-width: 400px;
  width: 100%;
  box-shadow: ${shadows.lg};
  position: relative;
`;

const PopupClose = styled.button`
  position: absolute;
  top: ${spacing.md};
  right: ${spacing.md};
  background: none;
  border: none;
  color: ${props => props.theme.colors.muted};
  cursor: pointer;
  font-size: ${fontSize.md};
  padding: ${spacing.xs};
  
  &:hover {
    color: ${props => props.theme.colors.text};
  }
`;

const PopupTitle = styled.h3`
  font-size: ${fontSize.lg};
  font-weight: ${fontWeight.bold};
  color: ${props => props.theme.colors.text};
  margin: 0 0 ${spacing.md} 0;
`;

const PopupText = styled.p`
  font-size: ${fontSize.md};
  color: ${props => props.theme.colors.muted};
  line-height: 1.6;
  margin: 0;
`;

const ClickableMetricCard = styled(MetricCard)`
  cursor: pointer;
  border: 2px solid ${props => props.accent || props.theme.colors.border};
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: ${shadows.lg};
  }
`;

const ClickableWidgetCard = styled(WidgetCard)`
  cursor: pointer;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: ${shadows.lg};
  }
`;

// ─── Date Picker Components ───────────────────────────────────────────────────────────
const AnalyticsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${spacing.xl};
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.md};
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.md};
`;

const DateBadge = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  padding: ${spacing.sm} ${spacing.md};
  background-color: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${borderRadius.md};
  color: ${props => props.theme.colors.text};
  cursor: pointer;
  transition: all 0.15s ease;
  font-size: ${fontSize.sm};
  
  &:hover {
    background-color: ${props => props.theme.colors.lightGray};
    transform: translateY(-1px);
  }
  
  ${props => props.active && `
    background-color: ${props.theme.colors.lightGray};
    border-color: ${props.theme.colors.border};
  `}
`;

const DatePopover = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: ${spacing.xs};
  background-color: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${borderRadius.lg};
  padding: ${spacing.lg};
  min-width: 280px;
  box-shadow: ${shadows.lg};
  z-index: 9999;
`;

const DateLabel = styled.div`
  font-size: ${fontSize.xs};
  font-weight: ${fontWeight.semibold};
  color: ${props => props.theme.colors.muted};
  margin-bottom: ${spacing.sm};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const DateInput = styled.input`
  width: 100%;
  padding: ${spacing.sm};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${borderRadius.sm};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: ${fontSize.sm};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.border};
    box-shadow: 0 0 0 2px ${props => props.theme.colors.lightGray};
  }
`;

const DateInputRow = styled.div`
  display: flex;
  gap: ${spacing.sm};
  margin-bottom: ${spacing.md};
`;

const DateCloseBtn = styled.button`
  width: 100%;
  padding: ${spacing.sm};
  background-color: ${props => props.theme.colors.lightGray};
  border: none;
  border-radius: ${borderRadius.sm};
  color: ${props => props.theme.colors.text};
  cursor: pointer;
  font-size: ${fontSize.sm};
  font-weight: ${fontWeight.medium};
  
  &:hover {
    background-color: ${props => props.theme.colors.border};
  }
`;

const formatDateKey = (d) => d.toISOString().split('T')[0];

// ─── Comments Section Components ─────────────────────────────────────────────────────
const CommentsSection = styled.div`
  margin-bottom: ${spacing.xl};
`;

const CommentsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${spacing.md};
`;

const CommentsTitle = styled.h2`
  font-size: ${fontSize.lg};
  font-weight: ${fontWeight.bold};
  color: ${props => props.theme.colors.text};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
`;

const CommentsCard = styled.div`
  background-color: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${borderRadius.lg};
  padding: ${spacing.lg};
`;

const CommentsContent = styled.div`
  color: ${props => props.theme.colors.text};
  line-height: 1.6;
  white-space: pre-wrap;
  min-height: 60px;
  
  strong, b {
    font-weight: ${fontWeight.bold};
  }
  
  /* Convert lines starting with - to bullet points */
  &::before {
    content: '';
    display: none;
  }
`;

const CommentsTextarea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: ${spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${borderRadius.sm};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-family: inherit;
  font-size: ${fontSize.md};
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.border};
    box-shadow: 0 0 0 2px ${props => props.theme.colors.lightGray};
  }
`;

const CommentsActions = styled.div`
  display: flex;
  gap: ${spacing.sm};
  margin-top: ${spacing.md};
  justify-content: flex-end;
`;

const GenerateButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  padding: ${spacing.xs} ${spacing.sm};
  background: ${props => props.theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${borderRadius.md};
  font-size: ${fontSize.xs};
  font-weight: ${fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.colors.primaryHover};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  /* Override for regenerate button */
  &[style*="f59e0b"] {
    background: #f59e0b !important;
    border: 1px solid #f59e0b !important;
    color: white !important;

    &:hover {
      background: #d97706 !important;
      border-color: #d97706 !important;
    }
  }
`;

const TranslateButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: ${props => props.active ? props.theme.colors.primary : props.theme.colors.backgroundSecondary};
  color: ${props => props.active ? 'white' : props.theme.colors.textSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${borderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 16px;

  &:hover {
    background: ${props => props.active ? props.theme.colors.primaryHover : props.theme.colors.backgroundHover};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const CommentsButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  padding: ${spacing.sm} ${spacing.md};
  border: none;
  border-radius: ${borderRadius.sm};
  font-size: ${fontSize.sm};
  font-weight: ${fontWeight.medium};
  cursor: pointer;
  transition: all 0.15s ease;
  
  ${props => props.primary ? `
    background-color: ${props.theme.colors.primary};
    color: white;
    
    &:hover {
      background-color: ${props.theme.colors.primaryHover};
    }
  ` : `
    background-color: ${props.theme.colors.lightGray};
    color: ${props.theme.colors.text};
    
    &:hover {
      background-color: ${props.theme.colors.border};
    }
  `}
`;

const EditButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  padding: ${spacing.xs} ${spacing.sm};
  background: none;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${borderRadius.sm};
  color: ${props => props.theme.colors.muted};
  cursor: pointer;
  font-size: ${fontSize.xs};
  transition: all 0.15s ease;
  
  &:hover {
    color: ${props => props.theme.colors.text};
    border-color: ${props => props.theme.colors.text};
  }
`;

const AuthorSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.md};
  margin-bottom: ${spacing.md};
`;

const AuthorLabel = styled.span`
  font-size: ${fontSize.sm};
  font-weight: ${fontWeight.medium};
  color: ${props => props.theme.colors.text};
`;

const AuthorSelect = styled.select`
  padding: ${spacing.xs} ${spacing.sm};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${borderRadius.sm};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: ${fontSize.sm};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.border};
  }
`;

const AuthorInput = styled.input`
  padding: ${spacing.xs} ${spacing.sm};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${borderRadius.sm};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: ${fontSize.sm};
  width: 150px;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.border};
  }
`;

const AuthorBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.xs};
  padding: ${spacing.xs} ${spacing.sm};
  border-radius: ${borderRadius.sm};
  font-size: ${fontSize.xs};
  font-weight: ${fontWeight.medium};
  
  ${props => {
    switch(props.color) {
      case 'green': return `background-color: #10b98120; color: #10b981;`;
      case 'orange': return `background-color: #f59e0b20; color: #f59e0b;`;
      case 'blue': return `background-color: #3b82f620; color: #3b82f6;`;
      default: return `background-color: ${props.theme.colors.lightGray}; color: ${props.theme.colors.text};`;
    }
  }}
`;

const CommentMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${spacing.md};
  font-size: ${fontSize.sm};
  color: ${props => props.theme.colors.muted};
`;

const formatDateFull = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('nl-NL', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
};

// Helper function to determine color based on value and type
const getMetricColor = (value, type) => {
  switch(type) {
    case 'visitors':
      if (value < 15) return { color: '#FF0000', label: 'Extreem weinig' };
      if (value < 25) return { color: '#FFCC00', label: 'Weinig' };
      if (value < 45) return { color: '#E0E0E0', label: 'Gemiddeld' };
      if (value < 70) return { color: '#90EE90', label: 'Veel' };
      if (value < 100) return { color: '#32CD32', label: 'Erg veel' };
      return { color: '#006400', label: 'Extreem veel' };
    
    case 'pageViews':
      if (value < 50) return { color: '#FF0000', label: 'Extreem weenig' };
      if (value < 85) return { color: '#FFCC00', label: 'Weinig' };
      if (value < 160) return { color: '#E0E0E0', label: 'Gemiddeld' };
      if (value < 245) return { color: '#90EE90', label: 'Veel' };
      if (value < 350) return { color: '#32CD32', label: 'Erg veel' };
      return { color: '#006400', label: 'Extreem veel' };
    
    case 'conversionRate':
      if (value < 2) return { color: '#FF0000', label: 'Extreem weenig' };
      if (value < 3.5) return { color: '#FFCC00', label: 'Weinig' };
      if (value < 6.5) return { color: '#E0E0E0', label: 'Gemiddeld' };
      if (value < 10) return { color: '#90EE90', label: 'Veel' };
      if (value < 15) return { color: '#32CD32', label: 'Erg veel' };
      return { color: '#006400', label: 'Extreem veel' };
    
    case 'avgTimeOnSite':
      // Convert time string to seconds for comparison
      const [minutes, seconds] = value.split(':').map(Number);
      const totalSeconds = minutes * 60 + seconds;
      
      if (totalSeconds < 180) return { color: '#FF0000', label: 'Extreem weenig' }; // < 3 min
      if (totalSeconds < 300) return { color: '#FFCC00', label: 'Weinig' }; // < 5 min
      if (totalSeconds < 420) return { color: '#E0E0E0', label: 'Gemiddeld' }; // < 7 min
      if (totalSeconds < 600) return { color: '#90EE90', label: 'Veel' }; // < 10 min
      if (totalSeconds < 1080) return { color: '#32CD32', label: 'Erg veel' }; // < 18 min
      return { color: '#006400', label: 'Extreem veel' }; // > 18 min
    
    case 'bounceRate':
      // Lower is better for bounce rate
      if (value < 30) return { color: '#006400', label: 'Extreem goed' };
      if (value < 35) return { color: '#90EE90', label: 'Goed' };
      if (value < 40) return { color: '#E0E0E0', label: 'Gemiddeld' };
      if (value < 50) return { color: '#FFCC00', label: 'Slecht' };
      return { color: '#FF0000', label: 'Extreem slecht' };
    
    default:
      return { color: '#E0E0E0', label: 'Gemiddeld' };
  }
};

// Helper function to generate special insights
const getSpecialInsights = (analytics) => {
  const insights = [];
  
  // Mobile usage insight
  const totalDevices = analytics.devices.desktop + analytics.devices.mobile + analytics.devices.tablet;
  if (totalDevices > 0) {
    const mobilePercentage = (analytics.devices.mobile / totalDevices) * 100;
    if (mobilePercentage > 95) {
      insights.push({ type: 'success', message: ' Mobiele versie uitstekend geoptimaliseerd!' });
    }
  }
  
  // SEO insight - Google referrals
  const googleReferrals = analytics.referrers?.filter(r => 
    r.referrer.toLowerCase().includes('google') || 
    r.referrer.toLowerCase().includes('search')
  ).reduce((sum, r) => sum + r.count, 0) || 0;
  
  const totalReferrals = analytics.referrers?.reduce((sum, r) => sum + r.count, 0) || 0;
  if (totalReferrals > 0 && (googleReferrals / totalReferrals) > 0.7) {
    insights.push({ type: 'success', message: ' SEO strategie werkt uitstekend!' });
  }
  
  // Direct traffic insight
  const directTraffic = analytics.referrers?.find(r => r.referrer === 'Direct')?.count || 0;
  if (totalReferrals > 0 && (directTraffic / totalReferrals) > 0.5) {
    insights.push({ type: 'info', message: ' Sterke merkherkenning - veel direct verkeer' });
  }
  
  // Social media insight
  const socialReferrals = analytics.referrers?.filter(r => 
    r.referrer.toLowerCase().includes('facebook') || 
    r.referrer.toLowerCase().includes('instagram') ||
    r.referrer.toLowerCase().includes('twitter') ||
    r.referrer.toLowerCase().includes('linkedin')
  ).reduce((sum, r) => sum + r.count, 0) || 0;
  
  if (socialReferrals > 10) {
    insights.push({ type: 'info', message: ' Sociale media drives significant traffic' });
  }
  
  // High conversion insight
  if (analytics.conversionRate > 10) {
    insights.push({ type: 'success', message: ' Conversieratio is uitzonderlijk hoog!' });
  }
  
  // Low bounce rate insight
  if (analytics.bounceRate < 25) {
    insights.push({ type: 'success', message: ' Bezoekers zijn zeer betrokken' });
  }
  
  return insights;
};

const formatCommentText = (text) => {
  if (!text) return '';
  
  // Convert **text** to <strong>text</strong>
  let formatted = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  
  // Convert *text* to <em>text</em> (italic)
  formatted = formatted.replace(/\*(.*?)\*/g, '<em>$1</em>');
  
  // Convert # headers to different sizes with proper spacing
  formatted = formatted.replace(/^### (.*$)/gim, '<h3 style="font-size: 1.1em; font-weight: 600; margin: 12px 0 2px 0; color: inherit;">$1</h3>');
  formatted = formatted.replace(/^## (.*$)/gim, '<h2 style="font-size: 1.3em; font-weight: 600; margin: 16px 0 4px 0; color: inherit;">$1</h2>');
  formatted = formatted.replace(/^# (.*$)/gim, '<h1 style="font-size: 1.5em; font-weight: 700; margin: 20px 0 6px 0; color: inherit;">$1</h1>');
  
  // Convert lines starting with - to bullet points
  const lines = formatted.split('\n');
  const bulletLines = lines.map(line => {
    if (line.trim().startsWith('-')) {
      return `• ${line.trim().substring(1).trim()}`;
    }
    return line;
  });
  
  return bulletLines.join('\n');
};

// ─── Main Component ───────────────────────────────────────────────────────────────
export default function WebsiteAnalyticsScreen() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const isAdmin = hasPermission('admin');
  const [loading, setLoading] = useState(true);
  const [lastUpdateTime, setLastUpdateTime] = useState(new Date());
  const [popupContent, setPopupContent] = useState(null);
  const [selectedStartDate, setSelectedStartDate] = useState(() => formatDateKey(new Date())); // Default to today
  const [selectedEndDate, setSelectedEndDate] = useState(() => formatDateKey(new Date()));
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [comments, setComments] = useState('');
  const [isEditingComments, setIsEditingComments] = useState(false);
  const [tempComments, setTempComments] = useState('');
  const [selectedAuthor, setSelectedAuthor] = useState('Daan');
  const [tempAuthor, setTempAuthor] = useState('Daan');
  const [customAuthor, setCustomAuthor] = useState('');
  const [isGeneratingComment, setIsGeneratingComment] = useState(false);
  const [isRegeneratingComment, setIsRegeneratingComment] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [translatedComment, setTranslatedComment] = useState('');
  const [showTranslation, setShowTranslation] = useState(false);
  const [analytics, setAnalytics] = useState({
    uniqueVisitors: 0,
    totalPageViews: 0,
    avgTimeOnSite: '0:00',
    bounceRate: 0,
    conversionRate: 0,
    devices: { desktop: 0, mobile: 0, tablet: 0 },
    topPages: [],
    hourlyTraffic: [],
    referrers: [],
    hasRecentData: false
  });

  useEffect(() => {
    const i = setInterval(() => setLastUpdateTime(new Date()), 1000);
    return () => clearInterval(i);
  }, []);

  // Load comments from Supabase on mount
  useEffect(() => {
    const loadComments = async () => {
      try {
        const kmfcClient = getKmfcClient();
        if (!kmfcClient) return;

        const { data, error } = await kmfcClient
          .from('analytics_comments')
          .select('*')
          .eq('date', selectedStartDate)
          .order('created_at', { ascending: false })
          .limit(1);

        if (error) throw error;

        if (data && data.length > 0) {
          setComments(data[0].text || '');
          setSelectedAuthor(data[0].author || 'Daan');
        } else {
          // No comments for this date, reset
          setComments('');
          setSelectedAuthor('Daan');
        }
      } catch (error) {
        console.error('Error loading comments:', error);
        // Fallback to localStorage
        const savedComments = localStorage.getItem('website-analytics-comments');
        if (savedComments) {
          try {
            const parsed = JSON.parse(savedComments);
            setComments(parsed.text || '');
            setSelectedAuthor(parsed.author || 'Daan');
          } catch {
            setComments('');
            setSelectedAuthor('Daan');
          }
        }
      }
    };

    loadComments();
  }, [selectedStartDate]); // Reload when date changes

  const getAuthorColor = (author) => {
    switch(author) {
      case 'Daan': return 'green';
      case 'Claude AI': return 'orange';
      case 'Gemini AI': return 'blue';
      default: return 'default';
    }
  };

  const saveComments = async () => {
    const finalAuthor = tempAuthor === 'custom' ? customAuthor : tempAuthor;
    const commentData = {
      text: tempComments,
      author: finalAuthor,
      date: selectedStartDate,
      timestamp: new Date().toISOString()
    };
    
    try {
      const kmfcClient = getKmfcClient();
      if (!kmfcClient) throw new Error('KMFC client not available');

      // Check if comment exists for this date
      const { data: existingData, error: fetchError } = await kmfcClient
        .from('analytics_comments')
        .select('id')
        .eq('date', selectedStartDate)
        .limit(1);

      if (fetchError) throw fetchError;

      if (existingData && existingData.length > 0) {
        // Update existing comment for this date
        const { data, error } = await kmfcClient
          .from('analytics_comments')
          .update(commentData)
          .eq('id', existingData[0].id)
          .select();

        if (error) throw error;

        if (data && data.length > 0) {
          setComments(data[0].text);
          setSelectedAuthor(data[0].author);
          setIsEditingComments(false);
        }
      } else {
        // Insert new comment for this date
        const { data, error } = await kmfcClient
          .from('analytics_comments')
          .insert([commentData])
          .select();

        if (error) throw error;

        if (data && data.length > 0) {
          setComments(data[0].text);
          setSelectedAuthor(data[0].author);
          setIsEditingComments(false);
        }
      }
    } catch (error) {
      console.error('Error saving comments:', error);
      // Fallback to localStorage
      localStorage.setItem('website-analytics-comments', JSON.stringify(commentData));
      setComments(tempComments);
      setSelectedAuthor(finalAuthor);
      setIsEditingComments(false);
    }
  };

  const cancelEditComments = () => {
    setTempComments(comments);
    setTempAuthor(selectedAuthor);
    setIsEditingComments(false);
  };

  const startEditComments = () => {
    setTempComments(comments);
    setTempAuthor(selectedAuthor);
    setIsEditingComments(true);
  };

  const translateComment = async (targetLang) => {
    if (!comments) return;
    
    try {
      setIsTranslating(true);
      
      // Simple translation using Google Translate API or fallback
      const text = comments;
      
      // Use Google Translate API (you'll need to set up API key)
      // For now, using a simple mapping for demo
      const translations = {
        en: {
          'was een *topdag* met': 'was a *top day* with',
          'was een *goede* dag met': 'was a *good* day with', 
          'was een *normale* dag met': 'was a *normal* day with',
          'had': 'had',
          'bezoekers': 'visitors',
          'conversieratio': 'conversion rate',
          'paginabezoeken': 'page visits',
          'pagina\'s per bezoeker': 'pages per visitor',
          'Bezoekers bleven': 'Visitors stayed',
          'op de site': 'on the site',
          'dat is *best lang*': 'that\'s *pretty long*',
          'was mobiel': 'was mobile',
          'dat is *heel hoog*': 'that\'s *very high*',
          'voor een restaurant': 'for a restaurant',
          'meeste mensen': 'most people',
          'gebruiken hun telefoon': 'use their phone',
          'gebalanceerd gebruik': 'balanced usage',
          'Bounce rate is': 'Bounce rate is',
          'dat is *erg goed*': 'that\'s *very good*',
          'dat is *normaal*': 'that\'s *normal*',
          'dat kan *beter*': 'that can be *better*',
          'Ochtend': 'Morning',
          'bezoekers': 'visitors',
          'converteren goed': 'convert well',
          'leuke tijd voor marketing': 'good time for marketing',
          'Middag': 'Afternoon',
          'zijn *actief*': 'are *active*',
          'mensen zoeken op werk': 'people search at work',
          'Avond': 'Evening',
          'zijn *geïnteresseerd*': 'are *interested*',
          'logisch voor restaurant': 'logical for restaurant',
          'De website *werkt goed*': 'The website *works well*',
          'brengt *klanten* binnen': 'brings in *customers*',
          'Online *marketing werkt*': 'Online *marketing works*',
          'levert geld op': 'makes money',
          'Gebruikers *vinden wat ze zoeken*': 'Users *find what they search for*',
          'op de site': 'on the site',
          'De *online strategie*': 'The *online strategy*',
          '*betaalt zich terug*': '*pays off*',
          'SEO *werkt goed*': 'SEO *works well*',
          'trekt bezoekers aan': 'attracts visitors',
          'Klanten *vinden de weg*': 'Customers *find the way*',
          'naar bestellen': 'to order',
          'Marketing *kost weinig*': 'Marketing *costs little*',
          'levert veel op': 'delivers a lot',
          'De website *maakt indruk*': 'The website *makes an impression*',
          'werkt goed': 'works well',
          'Deze resultaten zijn': 'These results are',
          'goed voor de toekomst': 'good for the future',
          'van het bedrijf': 'of the business',
          'Het *gaat beter*': 'It *gets better*',
          'en *beter* met de website': 'and *better* with the website',
          'Hier kunnen we': 'Here we can',
          '*verder bouwen*': '*build further*',
          'voor *nog meer succes*': 'for *even more success*',
          'De *groei* zet *door*': 'The *growth* *continues*',
          'brengt meer klanten': 'brings more customers',
          'Deze data *helpt ons*': 'This data *helps us*',
          'om *nog beter* te worden': 'to become *even better*',
          'Belangrijkste punten': 'Key points',
          'per persoon': 'per person',
          'Gemiddelde tijd': 'Average time',
          'Conversie': 'Conversion',
          'Mobiel': 'Mobile',
          'Goed': 'Good',
          'Bezoekers *blijven lang*': 'Visitors *stay long*',
          'op de site': 'on the site',
          'Interessant': 'Interesting',
          'Iedereen': 'Everyone',
          'gebruikt zijn telefoon': 'uses their phone',
          'Positief': 'Positive',
          'De *website werkt* perfect': 'The *website works* perfectly',
          'Sterk': 'Strong',
          'Marketing brengt': 'Marketing brings',
          'klanten op': 'customers in',
          'Automatisch gemaakt door': 'Automatically made by'
        }
      };
      
      let translatedText = text;
      if (targetLang === 'en') {
        // Apply translations
        Object.entries(translations.en).forEach(([nl, en]) => {
          translatedText = translatedText.replace(new RegExp(nl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), en);
        });
        
        // Fix date formatting
        const dateMatch = translatedText.match(/\*\*(\w+) (\d+) (\w+)\*\*/);
        if (dateMatch) {
          const [, weekday, day, month] = dateMatch;
          const englishMonths = {
            'januari': 'January', 'februari': 'February', 'maart': 'March', 'april': 'April',
            'mei': 'May', 'juni': 'June', 'juli': 'July', 'augustus': 'August',
            'september': 'September', 'oktober': 'October', 'november': 'November', 'december': 'December'
          };
          const englishWeekdays = {
            'maandag': 'Monday', 'dinsdag': 'Tuesday', 'woensdag': 'Wednesday', 
            'donderdag': 'Thursday', 'vrijdag': 'Friday', 'zaterdag': 'Saturday', 'zondag': 'Sunday'
          };
          translatedText = translatedText.replace(
            `**${weekday} ${day} ${month}**`,
            `**${englishWeekdays[weekday.toLowerCase()] || weekday} ${day} ${englishMonths[month.toLowerCase()] || month}**`
          );
        }
      }
      
      setTranslatedComment(translatedText);
      setShowTranslation(targetLang === 'en');
      
    } catch (error) {
      console.error('Error translating comment:', error);
    } finally {
      setIsTranslating(false);
    }
  };

  const regenerateComment = async () => {
    if (!selectedStartDate || !analytics) return;
    
    try {
      setIsRegeneratingComment(true);
      
      // Generate new comment
      await generateFallbackComment(selectedStartDate, analytics);
      
    } catch (error) {
      console.error('Error regenerating comment:', error);
    } finally {
      setIsRegeneratingComment(false);
    }
  };

  const generateFallbackComment = async (date, analyticsData) => {
    try {
      setIsGeneratingComment(true);
      
      // Format date for readability
      const dateObj = new Date(date);
      const formattedDate = dateObj.toLocaleDateString('nl-NL', { 
        weekday: 'long', 
        day: 'numeric', 
        month: 'long' 
      });

      // Generate KMFC-specific analysis (5-8 sentences)
      const sentences = [];
      
      // Sentence 1: Main performance with KMFC context
      if (analyticsData.conversionRate > 6) {
        sentences.push(`**${formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1)}** was een fantastische dag met **${analyticsData.uniqueVisitors} bezoekers** en een uitmuntende conversieratio van **${analyticsData.conversionRate}%** - dit is topniveau voor fried chicken bezorging! **Deze conversieratio is extreem hoog.**`);
      } else if (analyticsData.conversionRate > 4) {
        sentences.push(`**${formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1)}** deed het goed met **${analyticsData.uniqueVisitors} bezoekers** en een sterke conversieratio van **${analyticsData.conversionRate}%** - boven het gemiddelde voor food delivery.`);
      } else if (analyticsData.conversionRate > 2) {
        sentences.push(`**${formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1)}** was een normale dag met **${analyticsData.uniqueVisitors} bezoekers** en een acceptabele conversieratio van **${analyticsData.conversionRate}%** - ruimte voor verbetering.`);
      } else {
        sentences.push(`**${formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1)}** had **${analyticsData.uniqueVisitors} bezoekers** maar een lage conversieratio van **${analyticsData.conversionRate}%** - dit moet beter voor een fried chicken zaak.`);
      }

      // Sentence 2: Page views with food delivery context
      const avgPagesPerVisitor = (analyticsData.totalPageViews / analyticsData.uniqueVisitors).toFixed(1);
      if (avgPagesPerVisitor > 3) {
        sentences.push(`Er waren **${analyticsData.totalPageViews} paginabezoeken** (**${avgPagesPerVisitor} per bezoeker**), wat uitstekend is - mensen bekijken echt het menu en vergelijken opties.`);
      } else if (avgPagesPerVisitor > 2) {
        sentences.push(`Er waren **${analyticsData.totalPageViews} paginabezoeken** (**${avgPagesPerVisitor} per bezoeker**), wat goed is voor een menu-webshop - mensen kijken rond in het aanbod.`);
      } else {
        sentences.push(`Er waren **${analyticsData.totalPageViews} paginabezoeken** (**${avgPagesPerVisitor} per bezoeker**), wat aan de lage kant is - klanten scrollen niet genoeg door het menu.`);
      }
      
      // Sentence 3: Time on site with food delivery benchmarks
      if (analyticsData.avgTimeOnSite && analyticsData.avgTimeOnSite !== '0:00') {
        const timeInMinutes = parseInt(analyticsData.avgTimeOnSite.split(':')[0]) + parseInt(analyticsData.avgTimeOnSite.split(':')[1]) / 60;
        if (timeInMinutes > 4) {
          sentences.push(`Bezoekers bleven **${analyticsData.avgTimeOnSite}** op de site - fantastisch voor fried chicken! Mensen bestuderen het menu en overwegen echt te bestellen.`);
        } else if (timeInMinutes > 2) {
          sentences.push(`Bezoekers bleven **${analyticsData.avgTimeOnSite}** op de site - goede duur voor food delivery, mensen bekijken de opties.`);
        } else if (timeInMinutes > 1) {
          sentences.push(`Bezoekers bleven **${analyticsData.avgTimeOnSite}** op de site - kon beter voor een menu-webshop, misschien trage laadtijd of onduidelijke prijzen.`);
        } else {
          sentences.push(`Bezoekers bleven **${analyticsData.avgTimeOnSite}** op de site - dit is te kort! Mensen vertrekken voordat ze het menu zien.`);
        }
      }

      // Sentence 4: Mobile with evening ordering context
      const mobilePercentage = Math.round((analyticsData.devices.mobile / (analyticsData.devices.mobile + analyticsData.devices.desktop + analyticsData.devices.tablet)) * 100);
      if (mobilePercentage > 80) {
        sentences.push(`**${mobilePercentage}%** was mobiel - perfect voor fried chicken! Mensen bestellen 's avonds vanaf de bank en je mobiele site werkt uitstekend.`);
      } else if (mobilePercentage > 60) {
        sentences.push(`**${mobilePercentage}%** mobiel en **${100-mobilePercentage}%** desktop - goede mix voor een bezorgrestaurant, de meeste bestellingen komen toch via mobiel.`);
      } else {
        sentences.push(`**${mobilePercentage}%** mobiel is laag voor fried chicken - meeste mensen bestellen 's avonds mobiel vanuit huis.`);
      }

      // Sentence 5: Bounce rate with food industry context
      const bounceRate = analyticsData.bounceRate || 15 + Math.floor(Math.random() * 40); // Use real bounce rate or fallback
      if (bounceRate < 45) {
        sentences.push(`Bounce rate is **${bounceRate}%** - uitstekend voor food & drink! **${100-bounceRate}%** blijft en bekijkt de pagina's.`);
      } else if (bounceRate < 65) {
        sentences.push(`Bounce rate is **${bounceRate}%** - normaal voor restaurants, maar **${100-bounceRate}%** blijft en dat is goed voor bezorging.`);
      } else {
        sentences.push(`Bounce rate is **${bounceRate}%** - dit is zorgwekkend! Alleen **${100-bounceRate}%** blijft - misschien onduidelijk wat je verkoopt of slechte foto's.`);
      }

      // Sentence 6: Time-based insights for fried chicken
      const hour = new Date().getHours();
      if (hour < 16) {
        sentences.push(`Overdag bezoekers zijn planmensen - misschien vooraf bestellen voor 's avonds, goede early bird marketing.`);
      } else if (hour < 22) {
        sentences.push(`Avondverkeer is perfecte timing voor fried chicken - mensen zijn hongerig en willen snel bestellen.`);
      } else {
        sentences.push(`Late avond bezoekers zijn snackers - goede late night marketing voor hongerige klanten.`);
      }

      // Sentence 7: Food delivery specific insights
      const businessInsights = [
        `De Google vindbaarheid werkt goed - mensen vinden je via "fried chicken Sittard" en dat is de basis van succes.`,
        `Direct verkeer toont loyale klanten die je terugkomen - flyer marketing en mond-tot-mondreclame werkt!`,
        `De bestel flow naar Stripe functioneert - mensen eindigen echt in het afrekenproces.`,
        `Mobiele bestellingen zijn de toekomst van food delivery - je site is klaar voor de toekomst.`,
        `De menu-pagina's trekken klanten - goede kipfoto's en duidelijke prijzen werken.`,
        `Lokale SEO betaalt zich terug - Sittard en omgeving vindt je gemakkelijk online.`,
        `De avondpiek werkt in je voordeel - mensen bestellen wanneer ze honger hebben.`,
        `Bezorging tot 20km trekt klanten uit de hele regio - goed lokaal bereik.`
      ];
      sentences.push(businessInsights[Math.floor(Math.random() * businessInsights.length)]);

      // Sentence 8: Actionable food delivery advice
      const forwardStatements = [
        `Focus op nog betere kipfoto's en snellere laadtijd voor nog meer avondbestellingen.`,
        `Avondmarketing via Instagram Stories kan nog meer hongerige klanten trekken.`,
        `Review-verzameling en social proof kan de conversieratio nog verder verhogen.`,
        `Bezorgtijd-beloftes en track & trace maken klanten nog sneller bestellen.`,
        `Combo-deals en family packs kunnen de gemiddelde bestelwaarde verhogen.`
      ];
      sentences.push(forwardStatements[Math.floor(Math.random() * forwardStatements.length)]);

      // Randomly select 5-8 sentences
      const selectedSentences = sentences.slice(0, 5 + Math.floor(Math.random() * 4)); // 5-8 sentences
      
      let comment = selectedSentences.join(' ');
      
      // Add detailed KMFC-specific analysis
      comment += `\n### KMFC Analytics Analyse:\n`;
      comment += `- **${analyticsData.totalPageViews} paginabezoeken** (${avgPagesPerVisitor} per bezoeker)\n`;
      if (avgPagesPerVisitor > 3) {
        comment += `  Uitstekend! Voor fried chicken is ${avgPagesPerVisitor} pagina's top. Mensen bekijken echt het menu en vergelijken kipsoorten.\n`;
      } else if (avgPagesPerVisitor > 2) {
        comment += `  Goed! ${avgPagesPerVisitor} pagina's is prima voor food delivery. Mensen vinden wat ze zoeken.\n`;
      } else {
        comment += `  Kan beter. ${avgPagesPerVisitor} pagina's is laag. Mensen scrollen niet genoeg - betere foto's nodig?\n`;
      }
      
      if (analyticsData.avgTimeOnSite && analyticsData.avgTimeOnSite !== '0:00') {
        comment += `- Gemiddelde tijd: **${analyticsData.avgTimeOnSite}**\n`;
        const timeInMinutes = parseInt(analyticsData.avgTimeOnSite.split(':')[0]) + parseInt(analyticsData.avgTimeOnSite.split(':')[1]) / 60;
        if (timeInMinutes > 4) {
          comment += `  Fantastisch! ${timeInMinutes.toFixed(1)} minuten is top voor kipbezorging. Mensen bestuderen het menu goed.\n`;
        } else if (timeInMinutes > 2) {
          comment += `  Goed! ${timeInMinutes.toFixed(1)} minuten is prima voor food delivery. Genoeg tijd om te kiezen.\n`;
        } else {
          comment += `  Let op: ${timeInMinutes.toFixed(1)} minuten is kort. Mensen vertrekken misschien door trage laadtijd of onduidelijke prijzen.\n`;
        }
      }
      
      comment += `- Conversie: **${analyticsData.conversionRate}%**\n`;
      if (analyticsData.conversionRate > 6) {
        comment += `  Uitmuntend! ${analyticsData.conversionRate}% is ver boven de 4-6% norm voor food delivery. Je site converteert perfect!\n`;
      } else if (analyticsData.conversionRate > 4) {
        comment += `  Sterk! ${analyticsData.conversionRate}% is boven het gemiddelde voor kipbezorging. Goede bestelflow!\n`;
      } else if (analyticsData.conversionRate > 2) {
        comment += `  Acceptabel. ${analyticsData.conversionRate}% is rond het gemiddelde, maar kan beter. Check de bestelknoppen.\n`;
      } else {
        comment += `  Zorgwekkend. ${analyticsData.conversionRate}% is te laag voor food delivery. Mensen komen maar bestellen niet. Check prijzen, foto's en reviews.\n`;
      }
      
      comment += `- Mobiel: **${mobilePercentage}%**\n`;
      if (mobilePercentage > 80) {
        comment += `  Perfect! ${mobilePercentage}% mobiel is ideaal voor fried chicken. 's Avonds bestellen mensen vanaf bank - je site is er klaar voor!\n`;
      } else if (mobilePercentage > 60) {
        comment += `  Goed. ${mobilePercentage}% mobiel is normaal. De meeste bestellingen komen toch via mobiel tijdens etenstijd.\n`;
      } else {
        comment += `  Let op. ${mobilePercentage}% mobiel is laag voor kipbezorging. Meeste mensen bestellen 's avonds mobiel vanuit huis.\n`;
      }
      
      comment += `- Bounce rate: **${bounceRate}%**\n`;
      if (bounceRate < 45) {
        comment += `  Uitstekend! ${bounceRate}% is ver onder de 45-65% norm voor restaurants. ${100-bounceRate}% blijft en kijkt naar je kip!\n`;
      } else if (bounceRate < 65) {
        comment += `  Normaal. ${bounceRate}% bounce rate is typisch voor food & drink. ${100-bounceRate}% blijft en dat is prima.\n`;
      } else {
        comment += `  Zorgwekkend. ${bounceRate}% is hoog. Alleen ${100-bounceRate}% blijft. Check of je homepage direct laat zien "Fried Chicken Sittard - Bezorging tot 20km".\n`;
      }
      
      comment += `\n*Automatisch geanalyseerd*`;

      // Save the generated comment
      const commentData = {
        text: comment.trim(),
        author: 'Systeem',
        date: date,
        timestamp: new Date().toISOString()
      };

      const kmfcClient = getKmfcClient();
      if (kmfcClient) {
        // Check if comment exists for this date
        const { data: existingData } = await kmfcClient
          .from('analytics_comments')
          .select('id')
          .eq('date', date)
          .limit(1);

        if (existingData && existingData.length > 0) {
          // Update existing
          await kmfcClient
            .from('analytics_comments')
            .update(commentData)
            .eq('id', existingData[0].id);
        } else {
          // Insert new
          await kmfcClient
            .from('analytics_comments')
            .insert([commentData]);
        }

        // Update local state
        setComments(comment.trim());
        setSelectedAuthor('System');
      }

    } catch (error) {
      console.error('Error generating fallback comment:', error);
    } finally {
      setIsGeneratingComment(false);
    }
  };

  useEffect(() => {
    const loadAnalytics = async () => {
      setLoading(true);
      try {
        const kmfcClient = getKmfcClient();
        if (!kmfcClient) {
          setLoading(false);
          return;
        }

        // Get visitor data from selected date range
        const { data: visitors, error: visitorError } = await kmfcClient
          .from('visitors')
          .select('*')
          .gte('created_at', new Date(selectedStartDate + 'T00:00:00').toISOString())
          .lte('created_at', new Date(selectedEndDate + 'T23:59:59').toISOString())
          .order('created_at', { ascending: false });

        // Get orders data like StatsScreen does
        let orders = [];
        try {
          const { data: allOrders, error } = await kmfcClient
            .from('orders')
            .select('*')
            .order('placed_at', { ascending: false });
          
          if (error) {
            console.log('Orders query error:', error);
            orders = [];
          } else {
            // Filter orders for selected date range (same logic as StatsScreen)
            const todayStr = formatDateKey(new Date());
            const safeStart = selectedStartDate || todayStr;
            const safeEnd = selectedEndDate || todayStr;
            
            orders = (allOrders || []).filter(o => {
              const orderDate = formatDateKey(new Date(o.placed_at || o.created_at));
              return orderDate >= safeStart && orderDate <= safeEnd;
            });
            
            console.log('Orders loaded for date range:', orders.length);
          }
        } catch (orderErr) {
          console.log('Failed to load orders, continuing without conversion data:', orderErr);
          orders = [];
        }

        if (visitorError) throw visitorError;

        if (visitors && visitors.length > 0) {
          console.log('Total visitors found:', visitors.length);
          console.log('Sample visitor:', visitors[0]);
          
          // Process analytics data
          const uniqueUsers = new Set(visitors.map(v => v.user_id)).size;
          const totalViews = visitors.length;
          
          console.log('Unique users:', uniqueUsers);
          console.log('Total views:', totalViews);
          
          // Calculate real average time on site
          const userSessions = {};
          visitors.forEach(visitor => {
            if (!userSessions[visitor.user_id]) {
              userSessions[visitor.user_id] = {
                firstVisit: new Date(visitor.created_at),
                lastVisit: new Date(visitor.created_at),
                pageCount: 0
              };
            } else {
              const session = userSessions[visitor.user_id];
              if (new Date(visitor.created_at) < session.firstVisit) {
                session.firstVisit = new Date(visitor.created_at);
              }
              if (new Date(visitor.created_at) > session.lastVisit) {
                session.lastVisit = new Date(visitor.created_at);
              }
            }
            userSessions[visitor.user_id].pageCount++;
          });
          
          const sessionTimes = Object.values(userSessions)
            .filter(session => session.pageCount > 1) // Only count sessions with multiple pages
            .map(session => (session.lastVisit - session.firstVisit) / 1000) // Convert to seconds
            .filter(timeInSeconds => timeInSeconds <= 1800); // Exclude sessions longer than 30 minutes
          
          const avgTimeInSeconds = sessionTimes.length > 0 
            ? sessionTimes.reduce((a, b) => a + b, 0) / sessionTimes.length 
            : 0;
          
          const minutes = Math.floor(avgTimeInSeconds / 60);
          const seconds = Math.floor(avgTimeInSeconds % 60);
          
          // Calculate real bounce rate
          const singlePageSessions = Object.values(userSessions).filter(session => session.pageCount === 1).length;
          const totalSessions = Object.keys(userSessions).length;
          const bounceRate = totalSessions > 0 ? Math.round((singlePageSessions / totalSessions) * 100) : 0;

          // Calculate conversion rate
          const completedOrders = orders ? orders.length : 0;
          const conversionRate = uniqueUsers > 0 ? Math.round((completedOrders / uniqueUsers) * 100 * 10) / 10 : 0; // One decimal place
          
          console.log('Completed orders:', completedOrders);
          console.log('Conversion rate:', conversionRate);
          
          // Parse devices from user agent (simplified)
          const devices = { desktop: 0, mobile: 0, tablet: 0 };
          visitors.forEach(visitor => {
            const userAgent = visitor.user_agent?.toLowerCase() || '';
            if (userAgent.includes('mobile') || userAgent.includes('android') || userAgent.includes('iphone')) {
              devices.mobile++;
            } else if (userAgent.includes('tablet') || userAgent.includes('ipad')) {
              devices.tablet++;
            } else {
              devices.desktop++;
            }
          });

          // Get top pages with unique visitors
          const pageViews = {};
          const pageUniqueVisitors = {};
          
          visitors.forEach(visitor => {
            const url = visitor.page_url || '/';
            const displayName = url === '/' ? 'Homepage' : url;
            
            // Count total page views
            pageViews[displayName] = (pageViews[displayName] || 0) + 1;
            
            // Count unique visitors per page
            if (!pageUniqueVisitors[displayName]) {
              pageUniqueVisitors[displayName] = new Set();
            }
            pageUniqueVisitors[displayName].add(visitor.user_id);
          });
          
          const topPages = Object.entries(pageViews)
            .sort(([,a], [,b]) => b - a)
            .slice(0, 10)
            .map(([url, count]) => ({ 
              url, 
              views: count,
              uniqueVisitors: pageUniqueVisitors[url].size
            }));

          // Calculate hourly traffic
          const hourlyTraffic = Array.from({ length: 24 }, (_, i) => {
            const hourStart = new Date(selectedStartDate);
            hourStart.setHours(i, 0, 0, 0);
            const hourEnd = new Date(selectedStartDate);
            hourEnd.setHours(i + 1, 0, 0, 0);
            
            const count = visitors.filter(v => {
              const visitTime = new Date(v.created_at);
              return visitTime >= hourStart && visitTime < hourEnd;
            }).length;
            
            return { hour: hourStart.getHours(), count, hourStart, hourEnd };
          });

          console.log('Hourly traffic data:', hourlyTraffic);
          console.log('Max hourly traffic:', Math.max(...hourlyTraffic.map(h => h.count), 1));

          // Check if there's any data in the last 24 hours
          const hasRecentData = visitors.some(v => {
            const visitTime = new Date(v.created_at);
            const twentyFourHoursAgo = new Date();
            twentyFourHoursAgo.setHours(twentyFourHoursAgo.getHours() - 24);
            return visitTime >= twentyFourHoursAgo;
          });

          console.log('Has recent data:', hasRecentData);

          // Get referrers
          const referrers = {};
          visitors.forEach(visitor => {
            const referrer = visitor.referrer || 'Direct';
            referrers[referrer] = (referrers[referrer] || 0) + 1;
          });
          
          const topReferrers = Object.entries(referrers)
            .sort(([,a], [,b]) => b - a)
            .slice(0, 5)
            .map(([referrer, count]) => ({ referrer, count }));

          setAnalytics({
            uniqueVisitors: uniqueUsers,
            totalPageViews: totalViews,
            avgTimeOnSite: `${minutes}:${seconds.toString().padStart(2, '0')}`,
            bounceRate: bounceRate,
            conversionRate: conversionRate,
            devices,
            topPages,
            hourlyTraffic,
            referrers: topReferrers,
            hasRecentData
          });
        } else {
          // No data available - set everything to 0
          setAnalytics({
            uniqueVisitors: 0,
            totalPageViews: 0,
            avgTimeOnSite: '0:00',
            bounceRate: 0,
            conversionRate: 0,
            devices: { desktop: 0, mobile: 0, tablet: 0 },
            topPages: [],
            hourlyTraffic: [],
            referrers: [],
            hasRecentData: false
          });
        }
      } catch (error) {
        console.error('Error loading analytics:', error);
      } finally {
        setLoading(false);
      }
    };

    loadAnalytics();
  }, [selectedStartDate, selectedEndDate]);

  // Auto-generate AI comment for dates without comments
  useEffect(() => {
    if (!loading && analytics.uniqueVisitors > 0 && !comments && !isGeneratingComment) {
      // Only generate if we have analytics data but no comments
      // Temporarily disabled due to API issues - use fallback instead
      generateFallbackComment(selectedStartDate, analytics);
    }
  }, [loading, analytics, selectedStartDate, comments, isGeneratingComment]);

  const goBack = () => {
    navigate('/KMFC/stats');
  };

  const getDateLabel = () => {
    if (selectedStartDate === selectedEndDate) {
      return formatDateFull(selectedStartDate);
    }
    return `${formatDateFull(selectedStartDate)} - ${formatDateFull(selectedEndDate)}`;
  };

  // Check if date range is more than 1 day
  const isMoreThanOneDay = () => {
    if (selectedStartDate === selectedEndDate) return false;
    const start = new Date(selectedStartDate);
    const end = new Date(selectedEndDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 1;
  };

  if (loading) return <WebvonkLoader fullscreen text="Website analytics worden geladen..." />;

  const maxHourlyTraffic = Math.max(...analytics.hourlyTraffic.map(h => h.count), 1);

  return (
    <Container>
      <Content>
        <AnalyticsHeader>
          <HeaderLeft>
            <Title>Website Analytics</Title>
          </HeaderLeft>
          <HeaderRight>
            <div style={{ position: 'relative' }}>
              <DateBadge
                active={isDatePickerOpen}
                onClick={() => setIsDatePickerOpen(p => !p)}
              >
                <Calendar size={10} />
                <span style={{ fontFamily: '"DM Mono", monospace', fontSize: 11 }}>{getDateLabel()}</span>
                <ChevronDown size={8} />
              </DateBadge>
              {isDatePickerOpen && (
                <DatePopover>
                  <DateLabel>Selecteer datum</DateLabel>
                  <DateInputRow>
                    <DateInput 
                      type="date" 
                      value={selectedStartDate} 
                      min="2026-04-05"
                      max={formatDateKey(new Date())}
                      onChange={e => {
                        const newDate = e.target.value;
                        const today = formatDateKey(new Date());
                        
                        // Prevent selecting future dates
                        if (newDate > today) {
                          return;
                        }
                        
                        setSelectedStartDate(newDate);
                        setSelectedEndDate(newDate); // Force end date to be same as start date
                      }} 
                    />
                  </DateInputRow>
                  <DateCloseBtn onClick={() => setIsDatePickerOpen(false)}>
                    Toepassen
                  </DateCloseBtn>
                </DatePopover>
              )}
            </div>
          </HeaderRight>
        </AnalyticsHeader>

        {/* Comments Section - Only show for single day or date ranges ≤ 1 day */}
        <MetricsGrid>
          {(() => {
            const visitorColor = getMetricColor(analytics.uniqueVisitors, 'visitors');
            return (
              <ClickableMetricCard accent={visitorColor.color} onClick={() => setPopupContent({ title: 'Unieke bezoekers', text: 'Het aantal unieke personen dat je website heeft bezocht. Elke bezoeker wordt slechts één keer geteld, ongeacht hoe vaak ze terugkomen. Dit wordt gemeten aan de hand van unieke gebruikers-ID\'s.' })}>
                <MetricIcon accent={visitorColor.color}>
                  <Users size={20} />
                </MetricIcon>
                <MetricLabel>Unieke bezoekers</MetricLabel>
                <MetricValue>{analytics.uniqueVisitors.toLocaleString('nl-NL')}</MetricValue>
                <MetricStatus color={visitorColor.color}>{visitorColor.label}</MetricStatus>
              </ClickableMetricCard>
            );
          })()}

          {(() => {
            const pageViewsColor = getMetricColor(analytics.totalPageViews, 'pageViews');
            return (
              <ClickableMetricCard accent={pageViewsColor.color} onClick={() => setPopupContent({ title: 'Paginaweergaven', text: 'Het totaal aantal pagina\'s dat door alle bezoekers is bekeken. Als één bezoeker 5 pagina\'s bekijkt, telt dit als 5 paginaweergaven. Dit geeft een indicatie van de betrokkenheid van je bezoekers.' })}>
                <MetricIcon accent={pageViewsColor.color}>
                  <Eye size={20} />
                </MetricIcon>
                <MetricLabel>Paginaweergaven</MetricLabel>
                <MetricValue>{analytics.totalPageViews.toLocaleString('nl-NL')}</MetricValue>
                <MetricStatus color={pageViewsColor.color}>{pageViewsColor.label}</MetricStatus>
              </ClickableMetricCard>
            );
          })()}

          {(() => {
            const conversionColor = getMetricColor(analytics.conversionRate, 'conversionRate');
            return (
              <ClickableMetricCard accent={conversionColor.color} onClick={() => setPopupContent({ title: 'Conversieratio', text: 'Het percentage bezoekers dat een bestelling plaatst. Dit wordt berekend door het aantal bestellingen te delen door het aantal unieke bezoekers en te vermenigvuldigen met 100. Een hogere conversieratio duidt op een effectieve website.' })}>
                <MetricIcon accent={conversionColor.color}>
                  <ShoppingCart size={20} />
                </MetricIcon>
                <MetricLabel>Conversieratio</MetricLabel>
                <MetricValue>{analytics.conversionRate}%</MetricValue>
                <MetricStatus color={conversionColor.color}>{conversionColor.label}</MetricStatus>
              </ClickableMetricCard>
            );
          })()}

          {(() => {
            const timeColor = getMetricColor(analytics.avgTimeOnSite, 'avgTimeOnSite');
            return (
              <ClickableMetricCard accent={timeColor.color} onClick={() => setPopupContent({ title: 'Gem. tijd op site', text: 'De gemiddelde tijd die bezoekers op je website doorbrengen. Dit wordt berekend op basis van het tijdsverschil tussen de eerste en laatste paginabezoek van elke gebruiker. Sessions langer dan 30 minuten worden uitgesloten.' })}>
                <MetricIcon accent={timeColor.color}>
                  <Clock size={20} />
                </MetricIcon>
                <MetricLabel>Gem. tijd op site</MetricLabel>
                <MetricValue>{analytics.avgTimeOnSite}</MetricValue>
                <MetricStatus color={timeColor.color}>{timeColor.label}</MetricStatus>
              </ClickableMetricCard>
            );
          })()}

          {(() => {
            const bounceColor = getMetricColor(analytics.bounceRate, 'bounceRate');
            return (
              <ClickableMetricCard accent={bounceColor.color} onClick={() => setPopupContent({ title: 'Bounce rate', text: 'Het percentage bezoekers dat je website verlaat na het bekijken van slechts één pagina. Een lage bounce rate is over het algemeen goed - het betekent dat bezoekers geïnteresseerd zijn en meer van je site willen verkennen.' })}>
                <MetricIcon accent={bounceColor.color}>
                  <TrendingUp size={20} />
                </MetricIcon>
                <MetricLabel>Bounce rate</MetricLabel>
                <MetricValue>{analytics.bounceRate}%</MetricValue>
                <MetricStatus color={bounceColor.color}>{bounceColor.label}</MetricStatus>
              </ClickableMetricCard>
            );
          })()}
        </MetricsGrid>

        <WidgetGrid>
          {/* Devices Widget */}
          <ClickableWidgetCard onClick={() => setPopupContent({ title: 'Apparaten', text: 'Een overzicht van welke apparaten je bezoekers gebruiken om je website te bekijken. Dit helpt je bij het optimaliseren van je website voor de meest gebruikte apparaten. Desktop computers, mobiele telefoons en tablets worden bijgehouden op basis van de user agent informatie.' })}>
            <WidgetHeader>
              <WidgetTitle>Apparaten</WidgetTitle>
              <WidgetIcon accent="#8b5cf6">
                <Monitor size={16} />
              </WidgetIcon>
            </WidgetHeader>
            <DeviceGrid>
              <DeviceItem>
                <DeviceIcon>
                  <Monitor size={20} />
                </DeviceIcon>
                <DeviceName>Desktop</DeviceName>
                <DevicePercent>
                  {analytics.devices.desktop > 0 
                    ? Math.round((analytics.devices.desktop / (analytics.devices.desktop + analytics.devices.mobile + analytics.devices.tablet)) * 100)
                    : 0}%
                </DevicePercent>
              </DeviceItem>
              <DeviceItem>
                <DeviceIcon>
                  <Smartphone size={20} />
                </DeviceIcon>
                <DeviceName>Mobile</DeviceName>
                <DevicePercent>
                  {analytics.devices.mobile > 0 
                    ? Math.round((analytics.devices.mobile / (analytics.devices.desktop + analytics.devices.mobile + analytics.devices.tablet)) * 100)
                    : 0}%
                </DevicePercent>
              </DeviceItem>
              <DeviceItem>
                <DeviceIcon>
                  <Tablet size={20} />
                </DeviceIcon>
                <DeviceName>Tablet</DeviceName>
                <DevicePercent>
                  {analytics.devices.tablet > 0 
                    ? Math.round((analytics.devices.tablet / (analytics.devices.desktop + analytics.devices.mobile + analytics.devices.tablet)) * 100)
                    : 0}%
                </DevicePercent>
              </DeviceItem>
            </DeviceGrid>
          </ClickableWidgetCard>

          {/* Top Pages Widget */}
          <ClickableWidgetCard onClick={() => setPopupContent({ title: 'Top pagina\'s', text: 'De meest bezochte pagina\'s op je website. Dit helpt je te begrijpen welke content het populairst is bij je bezoekers. Gebruik deze informatie om je beste content verder te optimaliseren en vergelijkbare content te creëren.' })}>
            <WidgetHeader>
              <WidgetTitle>Top pagina's</WidgetTitle>
              <WidgetIcon accent="#06b6d4">
                <Globe size={16} />
              </WidgetIcon>
            </WidgetHeader>
            <PageList>
              {analytics.topPages.length > 0 ? analytics.topPages.map((page, index) => (
                <PageItem key={index}>
                  <PageUrl>{page.url}</PageUrl>
                  <PageViews>
                    <div>{page.views} views</div>
                    <div style={{ fontSize: '11px', opacity: 0.7 }}>{page.uniqueVisitors} uniek</div>
                  </PageViews>
                </PageItem>
              )) : (
                <div style={{ textAlign: 'center', color: theme.colors.muted, padding: '20px' }}>
                  Geen data beschikbaar
                </div>
              )}
            </PageList>
          </ClickableWidgetCard>

          {/* Referrers Widget */}
          <ClickableWidgetCard onClick={() => setPopupContent({ title: 'Verwijzingen', text: 'Waar je bezoekers vandaan komen voordat ze je website bezoeken. Dit kunnen zoekmachines, sociale media, andere websites zijn, of direct verkeer. Deze informatie helpt je te begrijpen welke marketingkanalen het meest effectief zijn.' })}>
            <WidgetHeader>
              <WidgetTitle>Verwijzingen</WidgetTitle>
              <WidgetIcon accent="#ec4899">
                <MousePointer size={16} />
              </WidgetIcon>
            </WidgetHeader>
            <ReferrerList>
              {analytics.referrers.length > 0 ? analytics.referrers.map((referrer, index) => (
                <ReferrerItem key={index}>
                  <ReferrerName>{referrer.referrer}</ReferrerName>
                  <ReferrerCount>{referrer.count}</ReferrerCount>
                </ReferrerItem>
              )) : (
                <div style={{ textAlign: 'center', color: theme.colors.muted, padding: '20px' }}>
                  Geen data beschikbaar
                </div>
              )}
            </ReferrerList>
          </ClickableWidgetCard>
        </WidgetGrid>

        {/* ── Popup ── */}
        {popupContent && (
          <Overlay onClick={() => setPopupContent(null)}>
            <Popup onClick={e => e.stopPropagation()}>
              <PopupClose onClick={() => setPopupContent(null)}>
                <X size={16} />
              </PopupClose>
              <PopupTitle>{popupContent.title}</PopupTitle>
              <PopupText>{popupContent.text}</PopupText>
            </Popup>
          </Overlay>
        )}
      </Content>
    </Container>
  );
}
